import { useEffect, useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const PROVIDERS = [
  {
    id: 'hubspot',
    label: 'HubSpot',
    description: 'Auto-link prospects to deals + sync deal status into your demo cards.',
    color: '#ff7a59',
  },
  {
    id: 'salesforce',
    label: 'Salesforce',
    description: 'Coming soon — same auto-link + sync flow as HubSpot.',
    color: '#00a1e0',
  },
];

/**
 * Per-provider CRM connection tile row. Drops into the Integrations
 * tab. Reads /api/integrations/crm/status on mount; renders one
 * tile per supported provider with the right CTA.
 *
 * Admin-only — non-admins see the tiles in read-only state with no
 * Connect button.
 */
export default function CrmIntegrationTiles({ authToken, isAdmin, onToast }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyProvider, setBusyProvider] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/integrations/crm/status`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Failed to load (${r.status})`);
      const data = await r.json();
      setStatus(data.providers || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-fetch status when the user lands back on the page after the
  // OAuth round-trip. The callback redirect appends
  // ?crm=connected&provider=hubspot (or ?crm=error&reason=...) to
  // the URL; we surface the result via toast + clear the query
  // param so a refresh doesn't re-fire it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const crm = params.get('crm');
    if (!crm) return;
    const provider = params.get('provider');
    const reason = params.get('reason');
    refresh();
    // Surface the result. Parent handler keeps the toast component
    // in App-level scope; we only fire the message.
    if (typeof onToast === 'function') {
      const providerLabel = provider === 'salesforce' ? 'Salesforce' : provider === 'hubspot' ? 'HubSpot' : 'CRM';
      if (crm === 'connected') {
        onToast(`✓ ${providerLabel} connected — deal status will sync automatically on every share.`);
      } else if (crm === 'error') {
        // Translate the most common reason codes into actionable
        // messaging. Anything else falls through to a generic line.
        const known = {
          token_exchange_failed: `Couldn't finish the ${providerLabel} connection. Try again, or check that your account has admin access.`,
          invalid_state: `Connection request expired. Please try Connect ${providerLabel} again.`,
          missing_code_or_state: `${providerLabel} connection was cancelled.`,
          unsupported_provider: `That CRM isn't supported yet.`,
        };
        onToast(known[reason] || `Couldn't connect ${providerLabel}: ${reason || 'unknown error'}`);
      }
    }
    // Strip the query so subsequent refreshes don't re-fire.
    params.delete('crm');
    params.delete('provider');
    params.delete('reason');
    const newSearch = params.toString();
    const url = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState(null, '', url);
  }, [refresh, onToast]);

  const startConnect = async (providerId) => {
    setError(null);
    setBusyProvider(providerId);
    try {
      const r = await fetch(`${API_BASE}/api/integrations/crm/${providerId}/connect`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Failed to start connect (${r.status})`);
      // Top-level redirect to provider's authorize URL — cleanest
      // path; pop-ups get blocked by some browsers.
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setBusyProvider(null);
    }
  };

  const disconnect = async (providerId) => {
    if (!window.confirm(`Disconnect ${providerId}? Existing deal links stay on file but no new ones will be created until you reconnect.`)) return;
    setError(null);
    setBusyProvider(providerId);
    try {
      const r = await fetch(`${API_BASE}/api/integrations/crm/${providerId}/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Failed to disconnect (${r.status})`);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyProvider(null);
    }
  };

  if (loading) {
    return <p style={{ color: 'var(--room-text-muted)', fontSize: 13 }}>Loading CRM status…</p>;
  }

  return (
    <div>
      {error && (
        <div style={{
          padding: '10px 12px', borderRadius: 8, marginBottom: 12,
          background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#dc2626', fontSize: 13,
        }}>
          {error}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {PROVIDERS.map((p) => {
          const s = status?.[p.id] || {};
          const connected = !!s.connected;
          const configured = !!s.configured;
          const busy = busyProvider === p.id;
          return (
            <div
              key={p.id}
              style={{
                background: 'var(--room-card)', borderRadius: 12, padding: 16,
                border: `1px solid ${connected ? p.color : 'var(--room-border, #e2e8f0)'}`,
                boxShadow: connected ? `0 2px 10px ${p.color}1a` : 'none',
                display: 'flex', flexDirection: 'column', gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: p.color, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 800,
                  }}>
                    {p.label.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--room-text-strong)' }}>{p.label}</div>
                    {connected && s.accountLabel && (
                      <div style={{ fontSize: 11, color: 'var(--room-text-muted)' }}>{s.accountLabel}</div>
                    )}
                  </div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  padding: '3px 10px', borderRadius: 100,
                  color: connected ? '#065f46' : (configured ? 'var(--room-text-body, #475569)' : '#92400e'),
                  background: connected ? '#d1fae5' : (configured ? 'var(--room-border-soft, #f1f5f9)' : '#fef3c7'),
                }}>
                  {connected ? 'Connected' : configured ? 'Not connected' : 'Coming soon'}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--room-text-muted)', lineHeight: 1.5 }}>
                {p.description}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 'auto' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {!isAdmin ? (
                    <span style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)' }}>Admin-only — ask your admin to connect.</span>
                  ) : connected ? (
                    <button
                      type="button"
                      onClick={() => disconnect(p.id)}
                      disabled={busy}
                      style={dangerBtn}
                    >
                      {busy ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                  ) : configured ? (
                    <button
                      type="button"
                      onClick={() => startConnect(p.id)}
                      disabled={busy}
                      style={primaryBtn(p.color)}
                    >
                      {busy ? 'Opening…' : `Connect ${p.label}`}
                    </button>
                  ) : (
                    <button type="button" disabled style={disabledBtn}>
                      Coming soon
                    </button>
                  )}
                </div>
                {/* Salesforce-specific gotcha: some orgs lock down
                    Connected Apps and require an org admin to whitelist
                    third-party apps before reps can OAuth in. Surfaced
                    inline so the customer hits 'oh, I need to talk to
                    my Salesforce admin' BEFORE clicking Connect and
                    bouncing off an opaque error. */}
                {p.id === 'salesforce' && isAdmin && configured && !connected && (
                  <span style={{ fontSize: 11, color: 'var(--room-text-muted)', lineHeight: 1.4 }}>
                    If your Salesforce org restricts Connected Apps, your Salesforce admin may need to pre-approve TrailerCast first.
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const primaryBtn = (color) => ({
  padding: '8px 14px', borderRadius: 8, border: 'none',
  background: color, color: '#fff',
  fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
});
const dangerBtn = {
  padding: '8px 14px', borderRadius: 8,
  border: '1px solid #fecaca', background: 'var(--room-card)',
  color: '#dc2626', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};
const disabledBtn = {
  padding: '8px 14px', borderRadius: 8,
  border: '1px solid var(--room-border)', background: 'var(--room-surface)',
  color: 'var(--room-text-subtle, #94a3b8)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'not-allowed',
};
